/**
 * Role form — ONE surface for viewing and editing a role. Same layout/chrome
 * in both states; only editability flips. Commit names the blast radius.
 * Also the entry point for creating a brand-new role (detail.id undefined).
 *
 * DUMB atomic SchmancyElement: all presentation is the `roleView` computed;
 * the view reads it and writes intents (roleDraft / rolesState / detailState /
 * members.state's membersAction$ channel — members.sync.ts, web-side,
 * performs the write). Derives nothing in render().
 *
 * `standalone` (set by select-role.view.ts's "+ Create new role" link, nested
 * over whatever overlay is already open) skips detailState entirely and
 * closes itself instead — location-form.view.ts's dispatched-`close` pattern.
 */
import { overflowWithin } from "@mhmo91/schmancy/directives";
import "@mhmo91/schmancy/form/fields/typography";
import { awaitByRequestId$ } from "@mhmo91/rx";
import { SchmancyElement, schmancyElement } from "@mhmo91/schmancy/mixins";
import { confirm } from "@mhmo91/schmancy/overlay";
import type { Permission } from "@momo/types";
import { html, type TemplateResult } from "lit";
import { property, state } from "lit/decorators.js";
import { when } from "lit/directives/when.js";
import { firstValueFrom, takeUntil } from "rxjs";
import { activeActor } from "../../auth/actor.state.ts";
import { LocalizeMixin } from "../../i18n/localize.ts";
import {
	detailState,
	membersAction$,
	membersResult$,
	membersState,
	roleDraft,
	rolesState,
	roleView,
} from "../members.state.ts";
import "./permission-picker.view.ts";

@schmancyElement("role-form")
export class RoleForm extends LocalizeMixin(SchmancyElement) {
	/** True when mounted nested (from-invite create) rather than via the members detailState reconciler. */
	@property({ type: Boolean }) standalone = false;
	@state() private pending: "save" | "delete" | null = null;
	@state() private failed: "save" | "delete" | null = null;

	private close(): void {
		this.dispatchEvent(
			new CustomEvent("close", { bubbles: true, composed: true }),
		);
	}

	private async commitRole(): Promise<void> {
		if (this.pending) return;
		const detail = detailState.value;
		if (!this.standalone && detail.kind !== "role") return;
		const { name: draftName, perms } = roleDraft.value;
		if (!draftName.trim() || perms.size === 0) return;
		const existingId =
			!this.standalone && detail.kind === "role" ? detail.id : undefined;
		const permissions = [...perms];
		const name = draftName.trim();
		this.pending = "save";
		try {
			if (existingId) {
				const affected = membersState.value.filter(
					(member) => member.roleId === existingId,
				).length;
				if (
					!(await confirm({
						title: this.t("admin.members.role.saveConfirmTitle"),
						message:
							affected === 1
								? this.t("admin.members.role.saveConfirmMessageOne")
								: this.t("admin.members.role.saveConfirmMessageOther", {
										count: affected,
									}),
						confirmText: this.t("admin.common.saveChanges"),
						cancelText: this.t("admin.common.keepEditing"),
					}))
				)
					return;
			}
			const requestId = crypto.randomUUID();
			const answer = firstValueFrom(
				awaitByRequestId$(membersResult$, requestId).pipe(
					takeUntil(this.disconnecting),
				),
			);
			membersAction$.next(
				existingId
					? {
							kind: "editRole",
							requestId,
							roleId: existingId,
							patch: { name, permissions },
						}
					: { kind: "createRole", requestId, name, permissions },
			);
			const result = await answer;
			if (!result.ok || (!existingId && !result.roleId)) {
				this.failed = "save";
				return;
			}
			const id = existingId ?? result.roleId;
			if (!id) return;
			const canonical = rolesState.value.find((role) => role.id === id);
			rolesState.replace([
				...rolesState.value.filter((role) => role.id !== id),
				{ ...canonical, id, name, permissions, isBuiltIn: false },
			]);
			roleDraft.replace({
				roleId: undefined,
				editing: false,
				name: "",
				perms: new Set(),
			});
			if (this.standalone) this.close();
			else if (!existingId) detailState.replace({ kind: "role", id });
		} catch {
			this.failed = "save";
		} finally {
			this.pending = null;
		}
	}

	private async handleDeleteRole(): Promise<void> {
		if (this.pending) return;
		const detail = detailState.value;
		if (detail.kind !== "role" || !detail.id) return;
		const roleId = detail.id;
		const memberCount = membersState.value.filter(
			(member) => member.roleId === roleId,
		).length;
		if (memberCount > 0) {
			this.failed = "delete";
			return;
		}
		this.pending = "delete";
		try {
			if (
				!(await confirm({
					title: this.t("admin.members.role.deleteConfirmTitle"),
					message: this.t("admin.members.role.deleteConfirmMessage"),
					confirmText: this.t("admin.common.delete"),
					cancelText: this.t("admin.common.cancel"),
					variant: "danger",
				}))
			)
				return;
			const requestId = crypto.randomUUID();
			const answer = firstValueFrom(
				awaitByRequestId$(membersResult$, requestId).pipe(
					takeUntil(this.disconnecting),
				),
			);
			membersAction$.next({ kind: "deleteRole", requestId, roleId });
			if (!(await answer).ok) {
				this.failed = "delete";
				return;
			}
			rolesState.replace(rolesState.value.filter((role) => role.id !== roleId));
			detailState.replace({ kind: "none" });
		} catch {
			this.failed = "delete";
		} finally {
			this.pending = null;
		}
	}

	/** Enter edit — seed the isolated draft from the canonical role. */
	private startEditing(): void {
		const detail = detailState.value;
		const canonical =
			detail.kind === "role" && detail.id
				? rolesState.value.find((r) => r.id === detail.id)
				: undefined;
		roleDraft.replace({
			roleId: detail.kind === "role" ? detail.id : undefined,
			name: canonical?.name ?? "",
			perms: new Set(canonical?.permissions ?? []),
			editing: true,
		});
	}

	override render(): TemplateResult {
		// Standalone: always a from-scratch draft, never detailState-derived —
		// the exact shape roleView produces for its own create-mode branch.
		const draft = roleDraft.value;
		const view = this.standalone
			? {
					editing: true,
					name: draft.name,
					selected: draft.perms,
					granted: draft.perms.size,
					affected: 0,
					canSave: draft.name.trim().length > 0 && draft.perms.size > 0,
					isBuiltIn: false,
				}
			: roleView.get();
		if (!view) return html``;
		const { editing, name, selected, granted, affected, canSave, isBuiltIn } =
			view;
		const detail = detailState.value;
		const canManageRoles =
			activeActor.get()?.permissions.includes("tickets.roles.manage") ?? false;
		// Edit toggle only on an EXISTING role — a create form is already editing;
		// an Edit switch there is meaningless.
		const showEditToggle =
			!this.standalone &&
			canManageRoles &&
			!isBuiltIn &&
			detail.kind === "role" &&
			!!detail.id;
		const assignedMembers =
			detail.kind === "role" && detail.id
				? membersState.value.filter((member) => member.roleId === detail.id)
						.length
				: 0;
		const showDelete =
			!this.standalone &&
			canManageRoles &&
			!isBuiltIn &&
			!editing &&
			detail.kind === "role" &&
			!!detail.id;

		return html`
		<div class="flex flex-col h-full max-h-[85vh]">
			<schmancy-form
				class="contents"
				@submit=${() => {
					// Enter's own blur lands the name in roleDraft synchronously, ahead
					// of this render's `canSave` snapshot — re-derive it live instead.
					const fresh = this.standalone
						? roleDraft.value.name.trim().length > 0 &&
							roleDraft.value.perms.size > 0
						: (roleView.get()?.canSave ?? false);
					if (fresh) this.commitRole();
				}}
			>
				<div ?inert=${this.pending !== null} class="flex items-center gap-3 px-5 pt-5 pb-3">
					<div class="min-w-0 flex-1 flex flex-col gap-1">
						<schmancy-typography-field
							type="title"
							token="md"
							placeholder=${this.t("admin.members.role.namePlaceholder")}
							class="min-w-0"
							.value=${name}
							?readonly=${!editing}
							?autofocus=${this.standalone || editing}
							@input=${(e: CustomEvent<{ value: string }>) => roleDraft.set({ name: e.detail.value })}
						></schmancy-typography-field>
						${when(
							isBuiltIn,
							() => html`
								<div class="flex items-center gap-1">
									<schmancy-icon class="text-surface-onVariant text-sm" aria-hidden="true">lock</schmancy-icon>
									<schmancy-typography type="body" token="xs" class="text-surface-onVariant">
										${this.t("admin.members.role.builtInNotice")}
									</schmancy-typography>
								</div>
							`,
						)}
					</div>

					${when(
						showEditToggle,
						() => html`
							<div class="flex items-center gap-2 shrink-0">
								<schmancy-typography type="label" token="sm" class="text-surface-onVariant">${this.t("admin.members.role.editLabel")}</schmancy-typography>
								<schmancy-switch
									.checked=${editing}
									aria-label=${this.t("admin.members.role.toggleEditAriaLabel")}
									@change=${(e: CustomEvent<{ value: boolean }>) => {
										if (e.detail.value === editing) return;
										if (e.detail.value) this.startEditing();
										else
											roleDraft.replace({
												roleId: undefined,
												editing: false,
												name: "",
												perms: new Set(),
											});
									}}
								></schmancy-switch>
							</div>
						`,
					)}

					<schmancy-icon-button
						icon="close"
						variant="text"
						aria-label=${this.t("admin.members.role.closeAriaLabel")}
						@click=${() => (this.standalone ? this.close() : detailState.replace({ kind: "none" }))}
					></schmancy-icon-button>
				</div>

				<div ?inert=${this.pending !== null} class="px-5 py-2 flex-1 min-h-0 flex flex-col gap-4">
					<div class="flex items-center justify-between gap-2 shrink-0">
						<schmancy-typography type="label" token="sm" class="text-surface-onVariant">${this.t("admin.members.common.permissionsLabel")}</schmancy-typography>
						<schmancy-typography type="label" token="sm" class=${granted > 0 ? "text-primary-default" : "text-surface-onVariant"}>
							${this.t("admin.members.role.grantedCount", { count: granted })}
						</schmancy-typography>
					</div>

					<!-- pb clears the ~72px sticky footer so the last row can scroll fully into view -->
					<div ${overflowWithin()} class="flex-1 min-h-0 flex flex-col gap-2 pb-24">
						<permission-picker
							?readonly=${!editing}
							.selected=${selected}
							@toggle-group=${(
								e: CustomEvent<{ perms: Permission[]; on: boolean }>,
							) => {
								const next = new Set(roleDraft.value.perms);
								if (e.detail.on) for (const p of e.detail.perms) next.add(p);
								else for (const p of e.detail.perms) next.delete(p);
								roleDraft.set({ perms: next });
							}}
							@toggle-perm=${(
								e: CustomEvent<{ perm: Permission; on: boolean }>,
							) => {
								const next = new Set(roleDraft.value.perms);
								if (e.detail.on) next.add(e.detail.perm);
								else next.delete(e.detail.perm);
								roleDraft.set({ perms: next });
							}}
						></permission-picker>
					</div>
				</div>

				${when(
					editing,
					() => html`
						<schmancy-surface opacity="translucent" elevated class="sticky bottom-0 px-5 py-4 pt-2 flex items-center justify-between gap-3">
							<schmancy-typography type="body" token="sm" class="text-surface-onVariant">
								${
									affected === 1
										? this.t("admin.members.role.affectedOne")
										: affected === 0
											? this.t("admin.members.role.affectedZero")
											: this.t("admin.members.role.affectedOther", {
													count: affected,
												})
								}
							</schmancy-typography>
							<div class="flex gap-2">
								<schmancy-button
									variant="outlined"
									?disabled=${this.pending !== null}
									@click=${() =>
										this.standalone
											? this.close()
											: roleDraft.replace({
													roleId: undefined,
													editing: false,
													name: "",
													perms: new Set(),
												})}
								>
									${this.t("admin.common.discard")}
								</schmancy-button>
								<schmancy-button variant="filled" .busy=${this.pending === "save"} color=${this.failed === "save" ? "error" : "primary"} ?disabled=${!canSave || this.pending === "delete"} @click=${() => this.commitRole()}>
									<span style="display:grid"><span style=${this.failed === "save" ? "grid-area:1/1;visibility:hidden" : "grid-area:1/1"} aria-hidden=${this.failed === "save" ? "true" : "false"}>${this.t("admin.common.saveChanges")}</span><span style=${this.failed === "save" ? "grid-area:1/1" : "grid-area:1/1;visibility:hidden"} aria-hidden=${this.failed === "save" ? "false" : "true"}>${this.t("admin.common.retrySave")}</span></span>
								</schmancy-button>
							</div>
						</schmancy-surface>
					`,
				)}

				${when(
					showDelete,
					() => html`
						<schmancy-surface opacity="translucent" elevated class="sticky bottom-0 px-5 py-4 pt-2 flex flex-col gap-2">
							<schmancy-button variant="outlined" color="error" .busy=${this.pending === "delete"} ?disabled=${this.pending === "save" || assignedMembers > 0} title=${assignedMembers > 0 ? this.t(assignedMembers === 1 ? "admin.members.role.deleteBlockedOne" : "admin.members.role.deleteBlockedOther", { count: assignedMembers }) : ""} class="w-full" @click=${() => this.handleDeleteRole()}>
								<schmancy-icon slot="prefix" aria-hidden="true">delete</schmancy-icon>
								<span style="display:grid"><span style=${this.failed === "delete" ? "grid-area:1/1;visibility:hidden" : "grid-area:1/1"} aria-hidden=${this.failed === "delete" ? "true" : "false"}>${this.t("admin.members.role.deleteButton")}</span><span style=${this.failed === "delete" ? "grid-area:1/1" : "grid-area:1/1;visibility:hidden"} aria-hidden=${this.failed === "delete" ? "false" : "true"}>${this.t("admin.common.retryDelete")}</span></span>
							</schmancy-button>
						</schmancy-surface>
					`,
				)}
			</schmancy-form>
		</div>
		`;
	}
}

declare global {
	interface HTMLElementTagNameMap {
		"role-form": RoleForm;
	}
}
